import AddIcon from '@mui/icons-material/Add';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { tr } from '../lib/i18n';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAllPages, api } from '../api/client';
import type { RequestRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { ErrorState } from '../components/common';
import { PageActions } from '../components/page-actions';
import { ActiveFormFilters, filtersQuery, FormFiltersButton, useSavedFilters } from '../components/FormFilters';
import { DataGrid, type GridColDef, type GridFilterModel } from '../components/data-grid';
import { RequestStatusChip, SourceChip, useActiveUsers } from '../components/org';
import { useToast } from '../components/Toast';
import { PersonAvatar } from '../components/AccountMenu';
import { apiLabel, CLOSED_REQUEST, EMPLOYEE_TYPE, fmtDay, range, REQUEST_STATUS, SOURCE } from '../lib/labels';

export function RequestsPage() {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [params] = useSearchParams();
  const canAssign = can('requests.assign');
  const [selected, setSelected] = useState<string[]>([]);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const users = useActiveUsers(canAssign);

  const [filters, setFilters] = useSavedFilters('DEMAND');
  const list = useQuery({ queryKey: ['requests', filters], queryFn: () => fetchAllPages<RequestRow>('/requests', filtersQuery(filters)), placeholderData: keepPreviousData });
  const rows = list.data ?? [];

  const columns = useMemo<GridColDef<RequestRow>[]>(() => [
    {
      field: 'ref_no',
      headerName: tr('المرجع', 'Reference'),
      width: 160,
      renderCell: ({ row: r }) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{r.ref_no}</Box>
          {r.is_possible_duplicate && <Chip size="small" color="warning" label={tr('مكرر؟', 'Duplicate?')} />}
        </Stack>
      ),
    },
    {
      field: 'contact_name',
      headerName: tr('العميل', 'Customer'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_, r) => `${r.contact_name} ${r.contact_phone}`,
      renderCell: ({ row: r }) => (
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.35 }}>
          <Typography variant="body2" fontWeight={500} noWrap>{r.contact_name}</Typography>
          <Typography variant="caption" display="block" color="text.secondary"><bdi>{r.contact_phone}</bdi></Typography>
        </Box>
      ),
    },
    {
      field: 'request_type_name',
      headerName: tr('المطلوب', 'Looking for'),
      flex: 1.1,
      minWidth: 180,
      valueGetter: (_, r) => `${r.request_type_name} ${r.property_type_name ?? ''} ${r.category_name ?? ''}`,
      renderCell: ({ row: r }) => (
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.35 }}>
          {apiLabel(r.request_type_name)}{r.property_type_name ? ` · ${apiLabel(r.property_type_name)}` : ''}
          <Typography variant="caption" display="block" color="text.secondary">{apiLabel(r.category_name) ?? ''}</Typography>
        </Box>
      ),
    },
    { field: 'locations', headerName: tr('المواقع', 'Locations'), flex: 1, minWidth: 160, valueGetter: (_, r) => r.locations ?? '—' },
    {
      field: 'budget_min',
      headerName: tr('الميزانية', 'Budget'),
      width: 160,
      valueGetter: (_, r) => range(r.budget_min, r.budget_max, ` ${r.currency}`),
    },
    {
      field: 'source',
      headerName: tr('المصدر', 'Source'),
      width: 130,
      type: 'singleSelect',
      valueOptions: Object.entries(SOURCE).map(([value, label]) => ({ value, label })),
      renderCell: ({ row: r }) => <SourceChip source={r.source} />,
    },
    {
      field: 'status',
      headerName: tr('الحالة', 'Status'),
      width: 140,
      type: 'singleSelect',
      valueOptions: Object.entries(REQUEST_STATUS).map(([value, label]) => ({ value, label })),
      renderCell: ({ row: r }) => <RequestStatusChip status={r.status} />,
    },
    {
      field: 'assigneeLabel',
      headerName: tr('المسؤول', 'Assignee'),
      width: 150,
      valueGetter: (_, r) => (r.assigned_to === me?.user.id ? tr('أنا', 'Me') : r.assigned_to_name ?? tr('غير معيّن', 'Unassigned')),
      renderCell: ({ row: r }) => (
        r.assigned_to === me?.user.id
          ? <Chip size="small" color="primary" label={tr('أنا', 'Me')} />
          : r.assigned_to_name
            ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
                <PersonAvatar name={r.assigned_to_name} seed={r.assigned_to ?? undefined} size={24} />
                <Typography variant="body2" noWrap>{r.assigned_to_name}</Typography>
              </Stack>
            )
            : <Chip size="small" color="warning" variant="outlined" label={tr('غير معيّن', 'Unassigned')} />
      ),
    },
    {
      field: 'duplicate',
      headerName: tr('تكرار', 'Duplicate'),
      width: 110,
      type: 'singleSelect',
      valueGetter: (_, r) => (r.is_possible_duplicate ? tr('مكرر محتمل', 'Possible duplicate') : tr('لا', 'No')),
      valueOptions: [tr('مكرر محتمل', 'Possible duplicate'), tr('لا', 'No')],
    },
    { field: 'created_at', headerName: tr('التاريخ', 'Date'), width: 140, valueFormatter: (v) => fmtDay(v as string) },
  ], [me]);

  const assignedTo = params.get('assignedTo') ?? '';
  const initialFilter = useMemo<GridFilterModel>(() => {
    if (assignedTo === 'unassigned') return { items: [{ id: 1, field: 'assigneeLabel', operator: 'equals', value: tr('غير معيّن', 'Unassigned') }] };
    if (assignedTo === 'me') return { items: [{ id: 1, field: 'assigneeLabel', operator: 'equals', value: tr('أنا', 'Me') }] };
    if (assignedTo) return { items: [{ id: 1, field: 'assigneeLabel', operator: 'equals', value: assignedTo }] };
    return { items: [] };
  }, [assignedTo]);

  const bulk = useMutation({
    mutationFn: (v: { toUserId: string | null; note: string }) => api<{ total: number; changed: number }>('/requests/bulk-assign', { method: 'POST', body: { ids: selected, toUserId: v.toUserId, note: v.note || undefined } }),
    onSuccess: (r) => {
      toast(tr(`تم تعيين ${r.changed} من ${r.total} طلب`, `Assigned ${r.changed} of ${r.total} requests`));
      setSelected([]);
      setMenuEl(null);
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: toastError,
  });

  return (
    <>
      <PageActions>
        <FormFiltersButton kind="DEMAND" value={filters} onChange={setFilters} />
        {can('form.submit') && can('requests.create') && <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to="/new?kind=DEMAND">{tr('إدخال جديد', 'New entry')}</Button>}
      </PageActions>
      <Card sx={{ flex: 1, minHeight: 420, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ActiveFormFilters kind="DEMAND" value={filters} onChange={setFilters} />
        {selected.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            sx={(theme) => ({ px: 2, py: 1.25, bgcolor: theme.palette.m3.secondaryContainer, color: theme.palette.m3.onSecondaryContainer, borderBottom: 1, borderColor: 'divider' })}
          >
            <Typography variant="body2" fontWeight={600}>{tr(`تم تحديد ${selected.length} طلب`, `${selected.length} requests selected`)}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="tonal" startIcon={<AssignmentIndIcon />} onClick={(e) => setMenuEl(e.currentTarget)}>{tr('تعيين المحدد', 'Assign selected')}</Button>
              <Button size="small" color="inherit" onClick={() => { setSelected([]); setMenuEl(null); }}>{tr('إلغاء التحديد', 'Clear selection')}</Button>
            </Stack>
          </Stack>
        )}
        {list.isError ? <Box sx={{ p: 2 }}><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box> : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={list.isLoading}
              checkboxSelection={canAssign}
              isRowSelectable={({ row }) => !CLOSED_REQUEST.includes(row.status)}
              rowSelectionModel={{ type: 'include', ids: new Set(selected) }}
              onRowSelectionModelChange={(m) => setSelected([...m.ids].map(String))}
              onRowClick={({ row }) => nav(`/requests/${row.id}`)}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
              initialState={{ filter: { filterModel: initialFilter } }}
              rowHeight={60}
              quickFilter={params.get('q')}
            />
          </Box>
        )}
      </Card>
      <Menu
        anchorEl={menuEl}
        open={!!menuEl && selected.length > 0}
        onClose={() => setMenuEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 420 } } }}
      >
        <ListSubheader disableSticky>{tr(`تعيين ${selected.length} طلب`, `Assign ${selected.length} requests`)}</ListSubheader>
        <MenuItem disabled={bulk.isPending} onClick={() => bulk.mutate({ toUserId: null, note: '' })}>{tr('بدون تعيين', 'Unassigned')}</MenuItem>
        <Divider />
        {(users.data?.items ?? []).map((u) => (
          <MenuItem key={u.id} disabled={bulk.isPending} onClick={() => bulk.mutate({ toUserId: u.id, note: '' })}>
            <ListItemAvatar>
              <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{u.full_name[0]}</Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={u.full_name}
              secondary={`${EMPLOYEE_TYPE[u.employee_type] ?? u.employee_type} · ${u.open_requests} ${tr('مفتوح', 'open')}`}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
